import { useEffect, useState } from "react";
import axios from "axios";
import { Truck, Store, Eye, Sun, Baby, Glasses } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  { icon: Sun, tk: "srv1t", dk: "srv1d" },
  { icon: Eye, tk: "srv2t", dk: "srv2d" },
  { icon: Baby, tk: "srv3t", dk: "srv3d" },
  { icon: Glasses, tk: "srv4t", dk: "srv4d" },
  { icon: Truck, tk: "srv5t", dk: "srv5d" },
];

export default function Services({ compact = false }) {
  const { t } = useLang();
  const [storePhotos, setStorePhotos] = useState(["/images/interno-negozio.webp"]);
  const [photoIdx, setPhotoIdx] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/site-config`)
      .then((r) => {
        const p = r.data?.store_photos;
        if (p?.length) setStorePhotos(p.map((x) => (x.startsWith("/") ? x : `${API}/files/${x}`)));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (storePhotos.length < 2) return;
    const id = setInterval(() => setPhotoIdx((i) => (i + 1) % storePhotos.length), 4000);
    return () => clearInterval(id);
  }, [storePhotos.length]);

  return (
    <section
      id="servizi"
      data-testid="services-section"
      className={`border-t border-[#1A1B18]/10 ${compact ? "py-14 md:py-20" : "py-20 md:py-28"}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#A86946] mb-4">{t("srv_overline")}</p>
          <h2 className="font-modern text-3xl sm:text-4xl font-medium tracking-tighter max-w-xl">
            {t("srv_title")}
          </h2>
        </Reveal>
        <div className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            {services.map((s, i) => (
              <Reveal key={s.tk} delay={i * 0.05}>
                <div
                  data-testid={`service-card-${i}`}
                  className="group flex items-start gap-4 py-5 border-t border-[#1A1B18]/10 last:border-b"
                >
                  <s.icon
                    size={18}
                    strokeWidth={1.5}
                    className="mt-0.5 shrink-0 text-[#A86946] transition-colors duration-300"
                  />
                  <div>
                    <h3 className="text-base md:text-lg font-medium tracking-tight group-hover:text-[#A86946] transition-colors duration-300">
                      {t(s.tk)}
                    </h3>
                    <p className="mt-1 text-sm text-[#1A1B18]/55 leading-relaxed max-w-md">{t(s.dk)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div
              data-testid="services-store-image"
              className="relative overflow-hidden rounded-2xl h-64 md:h-72 lg:h-full lg:min-h-[560px] group"
            >
              {storePhotos.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={t("srv_store_alt")}
                  data-testid={`services-store-photo-${i}`}
                  loading="eager"
                  decoding="async"
                  className={`absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-1000 ${
                    i === photoIdx ? "opacity-100 group-hover:scale-105" : "opacity-0"
                  }`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1B18]/60 via-transparent to-transparent flex items-end p-6 md:p-7">
                <div>
                  <h3 className="font-modern text-lg md:text-xl font-medium tracking-tight text-white">
                    {t("srv_store_t")}
                  </h3>
                  <p className="text-white/75 text-xs md:text-sm mt-1 max-w-xs leading-relaxed">{t("srv_store_d")}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
